import {
    database,
    readData,
} from '../../components/shared/database'
import {
    setDevicesData,
    setDeviceWriterDataAction,
} from '../../reducers/actions/devices'
import { setAppBusyAction } from '../../reducers/actions/app'
import { generateUUID } from '../../components/shared/uuid-generator'
import i18n from '../../locales/i18n'
import store from '../../store'
import { setSuperAdminLocationsIpAction } from '../../reducers/actions/super-admin'
import { processQuerySnapshot } from '../../components/shared/process-query-snapshot'
import type {
    Device,
    LocationIP,
} from '../../types/domain'

/**
 * Read devices based on user authorization
 *
 * @param filterMonitored - when null reads all devices regardless of monitoring status
 * @return {Promise}
 */
export const readDevices = (filterMonitored: boolean | null = null): Promise<void> => {
    const user = store.getState().auth.user as {
        superAdmin?: boolean
        uid: string
        company?: string
    }
    setAppBusyAction(true)

    // If user is super admin, take all items
    if (user.superAdmin) {
        return database('device')
            .where('isMonitored', filterMonitored === null ? '!=' : '==', filterMonitored)
            .get()
            .then(processQuerySnapshot)
            .then(result => {
                setDevicesData((result as Device[]).sort((aItem, bItem) => aItem.name.localeCompare(bItem.name)))
                setAppBusyAction(false)
                return undefined
            })
    }

    const findByCompanies = user.company ?
        database('device')
            .where('isMonitored', filterMonitored === null ? '!=' : '==', filterMonitored)
            .where('companies', 'array-contains', user.company || '')
            .get()
            .then(processQuerySnapshot)
        :
        new Promise<unknown[]>(resolve => resolve([]))

    const findByManagers = database('device')
        .where('isMonitored', filterMonitored === null ? '!=' : '==', filterMonitored)
        .where('managers', 'array-contains', user.uid)
        .get()
        .then(processQuerySnapshot)

    return Promise.all([findByCompanies, findByManagers])
        .then((arrayOfArrays) => {
            const mergedArray = ([] as unknown[]).concat(...arrayOfArrays) as Device[]

            return mergedArray
                .reduce((newListOfTags: Device[], current: Device) => {
                    if (!newListOfTags.some(item => item.uid === current.uid)) {
                        newListOfTags.push(current)
                    }
                    return newListOfTags
                }, [])
                .sort((aItem, bItem) => aItem.name.localeCompare(bItem.name))
        })
        .then(data => { setDevicesData(data) })
        .finally(() => setAppBusyAction(false))
}

/**
 * Listen and read single device based on uid
 *
 * @param {string} deviceUid
 */
export const readSingleDevice = (deviceUid: string): void => {
    database('device')
        .doc(deviceUid)
        .get()
        .then(querySnapshot => {
            const result = []
            result.push(querySnapshot.data())

            setDevicesData(result as Device[])
        })
}

/**
 * Device fields owned by Cloud Functions. Firestore rules reject client writes that change
 * them, so they are never sent back from the (possibly stale) form state.
 */
export const SERVER_MANAGED_DEVICE_FIELDS = ['nextCheckAt', 'consecutiveFailures'] as const

/**
 * Copy of the device without the server-managed fields.
 *
 * @param device - device form state
 * @return device data safe to write from the client
 */
export const withoutServerManagedFields = (device: Device): Device => {
    const writableDevice: Device = { ...device }

    SERVER_MANAGED_DEVICE_FIELDS.forEach((field) => {
        delete writableDevice[field]
    })

    return writableDevice
}

/**
 * Save device. If not exists, create new
 *
 * @param {Object} device
 * @return {Promise}
 */
export const saveDevice = (device: Device): Promise<void> => {
    if (!device.name || device.name === '') {
        return Promise.reject(i18n.t('device.form.nameRequired'))
    }

    if (!device.locationIP || device.locationIP === '') {
        return Promise.reject(i18n.t('device.form.locationIPRequired'))
    }

    if (device.uid) {
        return database('device')
            .doc(device.uid)
            .update(withoutServerManagedFields(device))
    }

    const deviceToSave: Device = {
        ...withoutServerManagedFields(device),
        uid: device.uid || generateUUID(),
        createdBy: (store.getState().auth.user as { uid: string }).uid,
        certificateIsCreating: device.createCertificate,
    }

    return database('device')
        .doc(deviceToSave.uid as string)
        .set(deviceToSave)
}

/**
 * Delete device
 *
 * @param {Object} device
 * @return {Promise}
 */
export const deleteDevice = (device: Device): Promise<void> => (
    database('device')
        .doc(device.uid as string)
        .delete()
)

/**
 * Recreate certificate
 *
 * @param {Object} device
 * @return {Promise}
 */
export const recreateCertificate = (device: Device): Promise<unknown> => (
    database('recreate_certificate')
        .add({
            deviceUid: device.uid,
        })
)

/**
 * Read locations IP from database
 *
 * @return {Promise}
 */
export const readLocationsIP = (): Promise<void> => (
    readData('location_ip')
        .then(result => setSuperAdminLocationsIpAction((result || []) as LocationIP[]))
)

/**
 * Save locationIP into database
 *
 * @param {Object} locationIP
 * @return {Promise}
 */
export const saveLocationIP = (locationIP: Partial<LocationIP>): Promise<void> => {
    const locationIPToSave: LocationIP = {
        name: locationIP.name || '',
        addressPrefix: locationIP.addressPrefix || '',
        ...locationIP,
        uid: locationIP.uid || generateUUID(),
    }

    return database('location_ip')
        .doc(locationIPToSave.uid as string)
        .set(locationIPToSave)
}

/**
 * Read device data writer data
 *
 * @param {string} deviceUid
 * @return {*}
 */
export const readDeviceDataWriter = (deviceUid: string): unknown => (
    database('device_writer')
        .where('deviceUid', '==', deviceUid)
        .onSnapshot(querySnapshot => {
            const result: unknown[] = []
            querySnapshot.forEach(document => {
                result.push(document.data())
            })

            setDeviceWriterDataAction(result as Parameters<typeof setDeviceWriterDataAction>[0])
        })
)
